"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useServer } from "@/components/providers/ServerProvider";

type ConsoleResponse = {
  lines: string[];
};

export default function ServerConsole() {
  const { state, players } = useServer();
  const serverActive =
    state === "starting" ||
    state === "online" ||
    state === "stopping";

  const [lines, setLines] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [selectedPlayer, setSelectedPlayer] =
    useState(0);

  const [command, setCommand] = useState("");
  const [sending, setSending] = useState(false);

  const consoleRef = useRef<HTMLDivElement>(null);

  function getMentionQuery() {
  const match = command.match(
    /@([A-Za-z0-9_]*)$/
  );

  return match
    ? match[1]
    : null;
}

const mentionQuery =
  getMentionQuery();

const filteredPlayers =
  mentionQuery !== null
    ? players.filter((player) =>
        player
          .toLowerCase()
          .startsWith(
            mentionQuery.toLowerCase()
          )
      )
    : [];

    function selectPlayer(
  player: string
) {
  setCommand((current) =>
    current.replace(
      /@[A-Za-z0-9_]*$/,
      `${player} `
    )
  );

  setSelectedPlayer(0);
}

function handleCommandKeyDown(
  event: React.KeyboardEvent<HTMLInputElement>
) {
  if (
    mentionQuery === null ||
    filteredPlayers.length === 0
  ) {
    return;
  }

  if (event.key === "ArrowDown") {
    event.preventDefault();

    setSelectedPlayer(
      (current) =>
        (current + 1) %
        filteredPlayers.length
    );
  }

  if (event.key === "ArrowUp") {
    event.preventDefault();

    setSelectedPlayer(
      (current) =>
        current === 0
          ? filteredPlayers.length - 1
          : current - 1
    );
  }

  if (
    event.key === "Tab" ||
    event.key === "Enter"
  ) {
    event.preventDefault();

    selectPlayer(
      filteredPlayers[
        selectedPlayer
      ]
    );
  }

  if (event.key === "Escape") {
    setSelectedPlayer(0);
  }
}

  // --------------------------------------------------
  // Fetch console output
  // --------------------------------------------------

  const fetchConsole = useCallback(async () => {
    try {
      const response = await fetch(
        "/api/minecraft/console",
        {
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          "Unable to retrieve console."
        );
      }

      const data: ConsoleResponse =
        await response.json();

      setLines(data.lines);
      setError(null);
    } catch (error) {
      console.error(error);

      setError(
        "Unable to retrieve server console."
      );
    }
  }, []);


  // --------------------------------------------------
  // Send console command
  // --------------------------------------------------

  async function sendCommand(
    event: FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    const value = command.trim();

    if (!value || sending) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await fetch(
        "/api/minecraft/command",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            command: value,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ??
            "Unable to send command."
        );
      }

      // Clear input after successful command
      setCommand("");

      // Immediately refresh console instead of
      // waiting for the next polling interval
      await fetchConsole();
    } catch (error) {
      console.error(error);

      setError(
        error instanceof Error
          ? error.message
          : "Unable to send command."
      );
    } finally {
      setSending(false);
    }
  }

  // --------------------------------------------------
  // Console polling
  // --------------------------------------------------

  useEffect(() => {
    if (!serverActive) {
      return;
    }

    const initialFetch = window.setTimeout(() => {
      void fetchConsole();
    }, 0);
    const consoleInterval = window.setInterval(
      () => {
        void fetchConsole();
      },
      1000
    );

    return () => {
      window.clearTimeout(initialFetch);
      window.clearInterval(
        consoleInterval
      );
    };
  }, [fetchConsole, serverActive]);

  // --------------------------------------------------
  // Auto-scroll when new lines arrive
  // --------------------------------------------------

  useEffect(() => {
    const element = consoleRef.current;

    if (!element) {
      return;
    }

    element.scrollTop =
      element.scrollHeight;
  }, [lines]);

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-[#181b1f] overflow-hidden">

      {/* Header */}
      <div className="px-4 sm:px-5 py-4 border-b border-white/10 flex items-center justify-between">

        <div>
          <h2 className="font-medium">
            Server Console
          </h2>

          <p className="text-xs text-gray-500 mt-1">
            Live Minecraft output
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500">

          <span className="h-2 w-2 rounded-full bg-green-400" />

          {serverActive
            ? "Live"
            : "Paused while offline"}

        </div>

      </div>

      {/* Console Output */}
      <div
        ref={consoleRef}
        className="console-scrollbar h-72 sm:h-80 overflow-y-auto overflow-x-auto bg-[#0d0f10] p-4 sm:p-5 font-mono text-xs sm:text-sm"
      >

        {lines.length === 0 && !error && (
          <p className="text-gray-600">
            No console output yet.
          </p>
        )}

        {error && (
          <p className="text-red-400 mb-2">
            {error}
          </p>
        )}

        {lines.map((line, index) => (
          <div
            key={`${index}-${line}`}
            className="text-gray-400 whitespace-pre-wrap break-words leading-6"
          >
            {line}
          </div>
        ))}

      </div>

      {/* Command Input */}
      <form
  onSubmit={sendCommand}
  className="relative border-t border-white/10 bg-[#111315] p-3 sm:p-4"
>

  {/* Player autocomplete */}
  {mentionQuery !== null &&
    filteredPlayers.length > 0 && (

      <div className="absolute bottom-full left-3 right-3 sm:left-4 sm:right-4 mb-2 rounded-xl border border-white/10 bg-[#181b1f] shadow-2xl overflow-hidden">

        <div className="px-3 py-2 border-b border-white/10">

          <p className="text-[10px] uppercase tracking-wider text-gray-500">
            Online players
          </p>

        </div>

        <div className="console-scrollbar max-h-48 overflow-y-auto">

          {filteredPlayers.map(
            (player, index) => (

              <button
                key={player}
                type="button"
                onClick={() =>
                  selectPlayer(player)
                }
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition ${
                  index === selectedPlayer
                    ? "bg-white/10 text-white"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`}
              >

                <div className="w-7 h-7 rounded-md bg-[#292e35] flex items-center justify-center text-xs">
                  👤
                </div>

                <span>
                  {player}
                </span>

                <span className="ml-auto text-[10px] text-green-400">
                  ONLINE
                </span>

              </button>

            )
          )}

        </div>

      </div>
    )}

  {/* Input */}
  <div className="flex gap-2">

    <span className="flex items-center font-mono text-green-400">
      &gt;
    </span>

    <input
      type="text"
      value={command}
      onChange={(event) => {
        setCommand(
          event.target.value
        );

        setSelectedPlayer(0);
      }}
      onKeyDown={
        handleCommandKeyDown
      }
      placeholder="Enter Minecraft command..."
      disabled={sending}
      autoComplete="off"
      spellCheck={false}
      className="flex-1 min-w-0 bg-transparent outline-none font-mono text-sm text-gray-300 placeholder:text-gray-600 disabled:opacity-50"
    />

    <button
      type="submit"
      disabled={
        sending ||
        !command.trim()
      }
      className="px-4 py-2 rounded-lg bg-[#292e35] hover:bg-[#333941] text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {sending
        ? "Sending..."
        : "Send"}
    </button>

  </div>

</form>

    </div>
  );
}